import { useQueryClient } from '@tanstack/react-query';

import React, { useId, useState } from 'react';

import { AddCircle, Code, Edit } from '@mui/icons-material';
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, TextField, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { EmbedDefinitionModel, EmbedDeploymentClient, EnvironmentEmbedClient, EnvironmentModel } from '../../../api/api';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import SidePanelList from '../../../components/Panels/SidePanelList';
import AccessCredentialHelp from './AccessCredentialHelp';
import EmbedDefinitionDialog from './EmbedDefinitionDialog';
import EmbedSnippet from './EmbedSnippet';
import EnvironmentTabs from './EnvironmentTabs';
import { useEmbedDefinitions, useEmbedDeployments } from './useEmbeds';

type Action = 'deploy' | 'remove' | 'rotate' | 'configure';
export default function EmbedsPanel({
	environments,
	initialEnvironment,
	initialDefinitionId,
	initialDeploymentId,
	initialAction,
	onClose,
	onReleaseCreated,
	top = 0,
}: {
	environments: EnvironmentModel[];
	initialEnvironment?: EnvironmentModel;
	initialDefinitionId?: string;
	initialDeploymentId?: string;
	initialAction?: 'remove';
	onClose: () => void;
	onReleaseCreated: (id: string, environment: EnvironmentModel) => void;
	top?: number;
}) {
	const tabId = useId();
	const definitions = useEmbedDefinitions();
	const [selectedId, setSelectedId] = useState(initialDefinitionId);
	const [deploymentId, setDeploymentId] = useState(initialDeploymentId);
	const [environmentId, setEnvironmentId] = useState(initialEnvironment?.id ?? environments[0]?.id ?? '');
	const environment = environments.find((value) => value.id === environmentId);
	const deployments = useEmbedDeployments(environmentId);
	const definition = definitions.data?.find((value) => value.id === selectedId);
	const deployment = deployments.data?.find((value) => (deploymentId ? value.id === deploymentId : value.definitionId === selectedId));
	const [editing, setEditing] = useState<EmbedDefinitionModel | null | undefined>();
	const [action, setAction] = useState<Action | undefined>(
		initialAction ?? (initialEnvironment && initialDefinitionId && !initialDeploymentId ? 'deploy' : undefined),
	);
	const [origins, setOrigins] = useState(deployment?.allowedOrigins.join('\n') ?? '');
	const [grace, setGrace] = useState(0);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [width, setWidth] = useState(500);
	const [detailsWidth, setDetailsWidth] = useState(500);
	const [collapsed, setCollapsed] = useState(false);
	const deploymentClient = useApiClient(EmbedDeploymentClient);
	const keyClient = useApiClient(EnvironmentEmbedClient);
	const queryClient = useQueryClient();
	const toast = useToastService();
	const refresh = async () => {
		await Promise.all([queryClient.invalidateQueries({ queryKey: ['embedDefinitions'] }), queryClient.invalidateQueries({ queryKey: ['embedDeployments'] })]);
	};
	const run = async (work: () => Promise<void>) => {
		setBusy(true);
		setError('');
		try {
			await work();
			await refresh();
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : 'Embed update failed.');
		} finally {
			setBusy(false);
		}
	};
	const startAction = (value: Action) => {
		setOrigins(deployment?.allowedOrigins.join('\n') ?? '');
		setGrace(0);
		setError('');
		setAction(value);
	};
	const execute = () =>
		void run(async () => {
			if (!environment) {
				return;
			}
			if (action === 'configure' && deployment) {
				await deploymentClient.configure(environment.id, deployment.id, {
					allowedOrigins: origins
						.split(/\r?\n/)
						.map((value) => value.trim())
						.filter(Boolean),
					isEnabled: deployment.isEnabled,
				});
				setAction(undefined);
				return;
			}
			if (action === 'rotate' && deployment) {
				await keyClient.rotate(environment.id, deployment.id, { graceMinutes: grace });
				setAction(undefined);
				toast.success('Embed key regenerated. Update the website snippet.');
				return;
			}
			const result =
				action === 'remove' && deployment
					? await deploymentClient.remove(environment.id, deployment.id)
					: await deploymentClient.deploy(environment.id, definition!.id, {
							allowedOrigins: origins
								.split(/\r?\n/)
								.map((value) => value.trim())
								.filter(Boolean),
						});
			setAction(undefined);
			onReleaseCreated(result.releasePlanId, environment);
		});
	const title = definition?.name ?? deployment?.name;
	return (
		<>
			<SidePanelList
				title="App Embeds"
				subtitle="Reusable public App integrations"
				items={definitions.data ?? []}
				columns={[
					{ id: 'name', label: 'Name' },
					{ id: 'code', label: 'Code' },
					{ id: 'views', label: 'Views', width: 60 },
				]}
				isLoading={definitions.isLoading}
				isFetching={definitions.isFetching}
				error={definitions.error}
				selectedItemId={selectedId}
				getItemId={(item) => item.id}
				onItemSelect={(item) => {
					setSelectedId(item.id);
					setDeploymentId(undefined);
				}}
				renderCell={(item, column) =>
					column === 'views' ? (
						<Chip size="small" label={item.allowedViewIds.length} />
					) : column === 'name' ? (
						<Stack direction="row" spacing={1}>
							<Code fontSize="small" />
							<Typography variant="body2">{item.name}</Typography>
						</Stack>
					) : (
						item.code
					)
				}
				width={width}
				setWidth={setWidth}
				resizeStorageKey="embedsPanel.width"
				defaultWidth={500}
				minWidth={360}
				maxWidth={900}
				isCollapsed={collapsed}
				collapsedWidth={48}
				onToggleCollapse={() => setCollapsed(!collapsed)}
				top={top}
				onClose={onClose}
				onRefresh={() => void refresh()}
				emptyMessage="Create an App Embed, then deploy it to an environment."
				headerActions={
					<IconButton title="Create App Embed" onClick={() => setEditing(null)}>
						<AddCircle />
					</IconButton>
				}
				footer={
					<Stack>
						<AccessCredentialHelp kind="embed" />
					</Stack>
				}
			/>
			{(definition || deployment) && (
				<SidePanelDetail
					title="App Embed Details"
					subtitle={title}
					isLoading={deployments.isLoading && !!environmentId}
					error={deployments.error}
					top={top}
					rightOffset={width}
					listPanelCollapsed={collapsed}
					width={detailsWidth}
					setWidth={setDetailsWidth}
					resizeStorageKey="embedDetailsPanel.width"
					defaultWidth={500}
					minWidth={360}
					maxWidth={1000}
					onClose={() => {
						setSelectedId(undefined);
						setDeploymentId(undefined);
					}}
					onRefresh={() => void refresh()}
					headerActions={
						definition && (
							<IconButton title="Edit App Embed" onClick={() => setEditing(definition)}>
								<Edit />
							</IconButton>
						)
					}
				>
					<Stack spacing={2}>
						<AccessCredentialHelp kind="embed" />
						{definition && (
							<>
								<Typography variant="body2">Code: {definition.code}</Typography>
								<Typography variant="body2">{definition.description}</Typography>
								<Typography variant="body2">Allowed views: {definition.allowedViewIds.length}</Typography>
							</>
						)}
						{!definition && <Alert severity="info">The App Embed definition is unavailable.</Alert>}
						<EnvironmentTabs
							environments={environments}
							environmentId={environment?.id}
							disabled={busy}
							label="App Embed environments"
							idPrefix={tabId}
							onChange={(id) => {
								setEnvironmentId(id);
								setDeploymentId(undefined);
								setAction(undefined);
								setError('');
							}}
						/>
						{!environment && <Alert severity="info">No environments available.</Alert>}
						{environment && (
							<Stack role="tabpanel" id={`${tabId}-panel`} aria-labelledby={`${tabId}-${environment.id}`} spacing={2}>
								{!deployment && <Alert severity="info">Deploy this App Embed from the environment node to generate its key and HTML snippet.</Alert>}
								{deployment?.updateAvailable && (
									<Alert severity="warning">Update available. Redeploy from the environment node to apply the latest definition.</Alert>
								)}
								{environment && !environment.changeSetId && <Alert severity="warning">Associate a ChangeSet with this environment before deployment.</Alert>}
								{deployment && (
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
										Actions
									</Typography>
								)}
								<Stack direction="row" spacing={1} flexWrap="wrap">
									{deployment && (
										<>
											<Button disabled={busy} onClick={() => startAction('rotate')}>
												Regenerate key
											</Button>
											<Button
												disabled={busy}
												onClick={() =>
													void run(async () => {
														await deploymentClient.configure(environmentId, deployment.id, {
															allowedOrigins: deployment.allowedOrigins,
															isEnabled: !deployment.isEnabled,
														});
													})
												}
											>
												{deployment.isEnabled ? 'Disable' : 'Enable'}
											</Button>
											<Button color="error" disabled={busy} onClick={() => startAction('remove')}>
												Remove
											</Button>
										</>
									)}
								</Stack>
								{deployment && (
									<>
										<Divider />
										<Chip label={deployment.isEnabled ? 'Enabled' : 'Disabled'} color={deployment.isEnabled ? 'success' : 'default'} />
										<Typography variant="subtitle2">Allowed website origins</Typography>
										{deployment.allowedOrigins.map((origin) => (
											<Typography variant="body2" key={origin}>
												{origin}
											</Typography>
										))}
										<Button disabled={busy} onClick={() => startAction('configure')}>
											Edit origins
										</Button>
										{deployment.releasePlanId && environment && (
											<Button onClick={() => onReleaseCreated(deployment.releasePlanId!, environment)}>View deployment</Button>
										)}
										<EmbedSnippet key={deployment.id} deployment={deployment} changeSetId={environment?.changeSetId} />
									</>
								)}
								{error && !action && <Alert severity="error">{error}</Alert>}
							</Stack>
						)}
					</Stack>
				</SidePanelDetail>
			)}
			{editing !== undefined && (
				<EmbedDefinitionDialog
					definition={editing}
					changeSetId={environment?.changeSetId}
					onClose={() => setEditing(undefined)}
					onSaved={(value) => {
						setEditing(undefined);
						setSelectedId(value.id);
						setDeploymentId(undefined);
						void refresh();
					}}
				/>
			)}
			<Dialog open={!!action} onClose={() => !busy && setAction(undefined)} fullWidth maxWidth="sm">
				<DialogTitle>
					{action === 'rotate'
						? 'Regenerate embed key'
						: action === 'remove'
							? 'Remove App Embed'
							: action === 'configure'
								? 'Edit website origins'
								: 'Deploy App Embed'}{' '}
					— {environment?.name}
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						{action === 'deploy' && (
							<>
								<Typography>
									{deployment
										? 'Redeployment preserves the publishable key and applies the latest definition.'
										: 'Deployment creates a key for this environment.'}{' '}
									A release plan will be queued.
								</Typography>
								<TextField
									label="Allowed website origins"
									multiline
									minRows={3}
									value={origins}
									onChange={(event) => setOrigins(event.target.value)}
									helperText="One exact HTTPS origin per line. Localhost may use HTTP."
								/>
							</>
						)}
						{action === 'configure' && (
							<TextField
								label="Allowed website origins"
								multiline
								minRows={3}
								value={origins}
								onChange={(event) => setOrigins(event.target.value)}
								helperText="One exact HTTPS origin per line. Updates apply immediately to this environment."
							/>
						)}
						{action === 'remove' && (
							<Typography>
								Remove this deployment from {environment?.name}? A release plan will revoke its key. The reusable definition remains available.
							</Typography>
						)}
						{action === 'rotate' && (
							<>
								<Typography>Update the website snippet after regeneration. Zero grace revokes the previous key and sessions immediately.</Typography>
								<TextField
									label="Grace period in minutes"
									type="number"
									value={grace}
									inputProps={{ min: 0, max: 1440 }}
									onChange={(event) => setGrace(Number(event.target.value))}
								/>
							</>
						)}
						{error && <Alert severity="error">{error}</Alert>}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button disabled={busy} onClick={() => setAction(undefined)}>
						Cancel
					</Button>
					<Button
						disabled={
							busy ||
							(action === 'deploy' && (!definition || !origins.trim())) ||
							(action === 'configure' && !origins.trim()) ||
							(action === 'rotate' && (!Number.isInteger(grace) || grace < 0 || grace > 1440))
						}
						onClick={execute}
					>
						{action === 'rotate' ? 'Regenerate' : action === 'remove' ? 'Remove' : action === 'configure' ? 'Save' : 'Deploy'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
